import { addMinutes } from "../date-math"
import { formatAddressInline } from "../countries"
import { formatPropertyAddressInlineWithUnit } from "../unit-labels"
import { internalOrderTypeLabel } from "../order-type-labels"
import { scheduleStatusLabels } from "../status-definitions"

export type EmployeeOption = { id: string; label: string; shortLabel?: string }

export type WorkingHoursDay = {
  weekday: number
  active: boolean
  start_time: string
  end_time: string
  windows?: Array<{ start_time: string; end_time: string }>
}

export type WorkingHoursByUser = Record<string, WorkingHoursDay[]>

export type ScheduleEventFormState = {
  employee_id: string
  event_type: ScheduleEventType
  flex_type: ScheduleEventFlexType
  status: ScheduleEventStatus
  start_at: string
  end_at: string
  timeframe_start_at: string | null
  timeframe_end_at: string | null
  all_day: boolean
  title: string
  description: string
  customer_note: string
  location_text: string
  property_id: string
  work_order_id: string
}

export type BuildScheduleEventFormParams = {
  event?: ScheduleEvent | null
  defaultEmployeeId?: string | null
  defaultStart?: Date | null
  defaultEnd?: Date | null
  defaultStatus?: ScheduleEventStatus | null
  defaultFlexType?: ScheduleEventFlexType | null
  defaultWorkOrderId?: string | null
  defaultPropertyId?: string | null
}

export type AvailabilityResult = {
  ok: boolean
  reason?: string
  type?: "working-hours" | "conflict"
}

export const statusLabels: Record<ScheduleEventStatus, string> = scheduleStatusLabels

export const eventTypeLabels: Record<ScheduleEventType, string> = {
  APPOINTMENT: "Termin",
  ABSENCE: "Abwesenheit",
  INTERNAL_BLOCK: "Interner Blocker",
}

export const APPOINTMENT_EARLIEST_START_MESSAGE =
  "Termine können frühestens ab morgen geplant werden."

const BERLIN_TIME_ZONE = "Europe/Berlin"
const berlinDatePartsFormatter = new Intl.DateTimeFormat("de-DE", {
  timeZone: BERLIN_TIME_ZONE,
  year: "numeric",
  month: "2-digit",
  day: "2-digit",
})

export const berlinDateKey = (value: Date) => {
  const parts = berlinDatePartsFormatter.formatToParts(value)
  const byType = Object.fromEntries(parts.map((part) => [part.type, part.value]))

  return `${byType.year}-${byType.month}-${byType.day}`
}

export const isAppointmentStartOnOrBeforeToday = (
  value: Date | null | undefined,
  now = new Date(),
) => {
  if (!value || Number.isNaN(value.getTime())) return false

  return berlinDateKey(value) <= berlinDateKey(now)
}

export const weekdayFromDate = (value: Date) => {
  const jsDay = value.getDay()

  return (jsDay + 6) % 7
}

export const parseMinutes = (value?: string | null) => {
  if (!value) return null

  const [hourStr, minuteStr] = value.split(":")
  const hour = Number(hourStr)
  const minute = Number(minuteStr ?? 0)

  if (Number.isNaN(hour) || Number.isNaN(minute)) return null

  if (hour < 0 || hour > 23 || minute < 0 || minute > 59) return null

  return hour * 60 + minute
}

export const validateWorkingWindow = (
  employeeId: string | null,
  start: Date,
  end: Date,
  workingHoursByUser?: WorkingHoursByUser,
): { ok: boolean; reason?: string } => {
  if (!employeeId) return { ok: true }

  const schedule = workingHoursByUser?.[employeeId]

  if (!schedule) return { ok: true }

  const weekday = weekdayFromDate(start)
  const entry = schedule.find((item) => item.weekday === weekday)

  if (!entry) return { ok: true }

  if (!entry.active) return { ok: false, reason: "Dieser Tag ist als arbeitsfrei hinterlegt." }

  const windows = (entry.windows?.length ? entry.windows : [entry])
    .map((window) => ({
      start: parseMinutes(window.start_time),
      end: parseMinutes(window.end_time),
    }))
    .filter(
      (window): window is { start: number; end: number } =>
        window.start !== null && window.end !== null && window.end > window.start,
    )

  if (!windows.length) {
    return { ok: true }
  }

  const startMinutes = start.getHours() * 60 + start.getMinutes()
  const endMinutes = end.getHours() * 60 + end.getMinutes()

  if (!windows.some((window) => startMinutes >= window.start && endMinutes <= window.end)) {
    return { ok: false, reason: "Zeit liegt außerhalb der Arbeitszeiten." }
  }

  return { ok: true }
}

export const toInputValue = (value: string | Date | null | undefined): string => {
  if (!value) return ""

  const date = value instanceof Date ? value : new Date(value)

  if (Number.isNaN(date.getTime())) return ""

  const local = new Date(date.getTime() - date.getTimezoneOffset() * 60000)

  return local.toISOString().slice(0, 16)
}

export const toIsoString = (value: string): string | null => {
  if (!value) return null

  const parsed = new Date(value)

  if (Number.isNaN(parsed.getTime())) return null

  return parsed.toISOString()
}

export const formatAddress = (property: Property) => {
  return formatAddressInline(property)
}

export const formatWorkOrder = (order: WorkOrder, propertyList: Property[]) => {
  const property = order.property_id
    ? propertyList.find((entry) => entry.id === order.property_id)
    : null
  const orderNo = order.order_number ? `#${order.order_number}` : "Auftrag"
  const descriptor = internalOrderTypeLabel(order) || ""
  const address = property
    ? formatPropertyAddressInlineWithUnit(
        property,
        order.property_unit_id,
        order.property_unit_label,
      )
    : ""

  return [orderNo, descriptor, address].filter(Boolean).join(" • ")
}

export const buildEmployeeOptions = (employees: EmployeeOption[]) =>
  employees.map((emp) => ({ name: emp.label, value: emp.id }))

export const buildPropertyOptions = (propertyList: Property[]) =>
  propertyList.map((property) => ({
    name: formatAddress(property),
    value: property.id,
  }))

export const buildWorkOrderById = (workOrders: WorkOrder[]) =>
  workOrders.reduce<Record<string, WorkOrder>>((acc, order) => {
    if (order?.id) acc[order.id] = order

    return acc
  }, {})

export const buildWorkOrderOptions = ({
  workOrders,
  propertyList,
  formWorkOrderId,
}: {
  workOrders: WorkOrder[]
  propertyList: Property[]
  formWorkOrderId: string
}) =>
  workOrders
    .filter((order) => {
      if (!order?.id) return false

      const normalizedStatus = String((order as any)?.status ?? "").toUpperCase()
      const isClosed = Boolean(normalizedStatus && normalizedStatus !== "OPEN")

      if (formWorkOrderId && order.id === formWorkOrderId) return true

      if (isClosed) return false

      return !order.current_schedule_event_id
    })
    .map((order) => ({
      name: formatWorkOrder(order, propertyList),
      value: order.id,
    }))

export const buildStatusOptions = (_planningMode: boolean) =>
  Object.entries(statusLabels).map(([value, name]) => ({ name, value }))

export const roundToStep = (date: Date, stepMinutes: number) => {
  const copy = new Date(date)
  const minutes = copy.getMinutes()
  const remainder = minutes % stepMinutes
  copy.setMinutes(minutes - remainder, 0, 0)

  return copy
}

export const computeDefaultRange = (
  start?: Date | null,
  end?: Date | null,
  stepMinutes = 30,
  durationMinutes = 60,
) => {
  const hasCustomStart = Boolean(start)
  const initialStart = start ? new Date(start) : roundToStep(new Date(), stepMinutes)
  const now = new Date()
  const startIsPast = initialStart.getTime() < now.getTime()
  const futureStart =
    hasCustomStart || !startIsPast ? initialStart : addMinutes(initialStart, stepMinutes)
  const baseEnd = end ? new Date(end) : addMinutes(futureStart, durationMinutes)

  return { start: futureStart, end: baseEnd }
}

const normalizeFlexType = (value?: ScheduleEventFlexType | null): ScheduleEventFlexType =>
  value === "SOFT" ? "SOFT" : ("HARD" as ScheduleEventFlexType)

const isAppointmentType = (value?: ScheduleEventType | null) =>
  (value ?? "APPOINTMENT") === "APPOINTMENT"

const cleanedOptionalText = (value?: string | null) => {
  const cleaned = String(value ?? "").trim()

  return cleaned || null
}

export const hasOverlap = (
  scheduleEvents: ScheduleEvent[],
  startIso: string,
  endIso: string,
  employeeId: string | null,
  currentId: string | null,
  candidate?: { flexType?: ScheduleEventFlexType | null; eventType?: ScheduleEventType | null },
) => {
  if (!employeeId) return false

  const start = new Date(startIso)
  const end = new Date(endIso)

  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return false

  const candidateFlexType = normalizeFlexType(candidate?.flexType ?? null)
  const candidateEventType = (candidate?.eventType ?? "APPOINTMENT") as ScheduleEventType

  if (!isAppointmentType(candidateEventType)) return false

  return scheduleEvents.some((existing) => {
    if (!existing.employee_id || existing.employee_id !== employeeId) return false

    if (existing.id === currentId) return false

    if (existing.status === "CANCELED" || existing.status === "NOSHOW") return false

    const existingStart = new Date(existing.start_at)
    const existingEnd = new Date(existing.end_at)

    if (Number.isNaN(existingStart.getTime()) || Number.isNaN(existingEnd.getTime())) return false

    const overlaps = existingStart < end && existingEnd > start

    if (!overlaps) return false

    const existingEventType = (existing as any)?.event_type as ScheduleEventType | undefined
    const existingFlexType = normalizeFlexType((existing as any)?.flex_type ?? null)

    if (!isAppointmentType(existingEventType) || !isAppointmentType(candidateEventType)) {
      return true
    }

    if (existingFlexType === "SOFT" && candidateFlexType === "HARD") return false

    if (existingFlexType === "HARD" && candidateFlexType === "SOFT") return false

    return true
  })
}

export const normalizeCandidateIds = (
  candidateEmployeeIds?: string[] | null,
  primaryId?: string | null,
) => {
  const base = Array.isArray(candidateEmployeeIds) ? candidateEmployeeIds.filter(Boolean) : []
  const unique = new Set(base)

  if (primaryId) unique.add(primaryId)

  return Array.from(unique)
}

export const checkEmployeeAvailability = ({
  employeeId,
  start,
  end,
  scheduleEvents,
  workingHoursByUser,
  currentEventId = null,
  eventType = "APPOINTMENT",
  flexType = "HARD",
}: {
  employeeId: string
  start: Date
  end: Date
  scheduleEvents: ScheduleEvent[]
  workingHoursByUser?: WorkingHoursByUser
  currentEventId?: string | null
  eventType?: ScheduleEventType
  flexType?: ScheduleEventFlexType
}): AvailabilityResult => {
  if (!isAppointmentType(eventType)) {
    return { ok: true }
  }

  const working = validateWorkingWindow(employeeId, start, end, workingHoursByUser)

  if (!working.ok) {
    return {
      ok: false,
      reason: working.reason ?? "Zeit liegt außerhalb der Arbeitszeiten.",
      type: "working-hours",
    }
  }

  const startIso = start.toISOString()
  const endIso = end.toISOString()

  if (
    hasOverlap(scheduleEvents, startIso, endIso, employeeId, currentEventId, {
      flexType,
      eventType,
    })
  ) {
    return {
      ok: false,
      reason: "Dieser Zeitraum überschneidet sich mit einem bestehenden Termin des Mitarbeiters.",
      type: "conflict",
    }
  }

  return { ok: true }
}

export type ScheduleSubmissionResult =
  | { ok: true; payload: ScheduleEventCreatePayload }
  | { ok: false; reason: string; resetStart?: string }

export const validateAndBuildPayload = ({
  form,
  event,
  defaultStatus,
  defaultFlexType,
  isAppointment,
  startIso,
  endIso,
  startDate,
  endDate,
  now,
  workingHoursByUser,
  scheduleEvents,
  candidateEmployeeIds,
  organizationId,
}: {
  form: ScheduleEventFormState
  event: ScheduleEvent | null
  defaultStatus: ScheduleEventStatus | null
  defaultFlexType?: ScheduleEventFlexType | null
  isAppointment: boolean
  startIso: string | null
  endIso: string | null
  startDate: Date | null
  endDate: Date | null
  now: Date
  workingHoursByUser?: WorkingHoursByUser
  scheduleEvents: ScheduleEvent[]
  candidateEmployeeIds?: string[] | null
  organizationId: string
}): ScheduleSubmissionResult => {
  if (!startIso || !endIso) {
    return { ok: false, reason: "Bitte gültige Start- und Endzeiten wählen." }
  }

  if (startDate && endDate && startDate >= endDate) {
    return { ok: false, reason: "Endzeit muss nach der Startzeit liegen." }
  }

  const targetFlexType: ScheduleEventFlexType =
    form.flex_type || (defaultFlexType ?? ("HARD" as ScheduleEventFlexType))

  if (targetFlexType === "SOFT" && form.event_type !== "APPOINTMENT") {
    return { ok: false, reason: "Flextermine sind nur für Termine erlaubt." }
  }

  if (!isAppointment && form.work_order_id) {
    return { ok: false, reason: "Nur Termine dürfen mit einem Auftrag verknüpft werden." }
  }

  void now
  void workingHoursByUser
  void scheduleEvents
  void candidateEmployeeIds

  const targetStatus: ScheduleEventStatus =
    form.status || event?.status || defaultStatus || ("RESERVED" as ScheduleEventStatus)
  const timeframeStartInput =
    targetFlexType === "SOFT" ? form.timeframe_start_at || form.start_at || startIso : startIso
  const timeframeEndInput =
    targetFlexType === "SOFT" ? form.timeframe_end_at || form.end_at || endIso : endIso
  const timeframeStart = timeframeStartInput ? toIsoString(timeframeStartInput) : null
  const timeframeEnd = timeframeEndInput ? toIsoString(timeframeEndInput) : null

  if (targetFlexType === "SOFT" && (!timeframeStart || !timeframeEnd)) {
    return { ok: false, reason: "Bitte ein gültiges Flex-Zeitfenster wählen." }
  }

  if (timeframeStart && timeframeEnd && new Date(timeframeStart) >= new Date(timeframeEnd)) {
    return { ok: false, reason: "Zeitfenster für Flextermin ist ungültig." }
  }

  const payload: ScheduleEventCreatePayload = {
    employee_id: form.employee_id || null,
    event_type: form.event_type,
    flex_type: targetFlexType,
    status: targetStatus,
    start_at: startIso,
    end_at: endIso,
    timeframe_start_at: timeframeStart || null,
    timeframe_end_at: timeframeEnd || null,
    all_day: false,
    title: isAppointment ? null : cleanedOptionalText(form.title),
    description: cleanedOptionalText(form.description),
    customer_note: cleanedOptionalText(form.customer_note),
    location_text: cleanedOptionalText(form.location_text),
    property_id: form.property_id || null,
    work_order_id: isAppointment ? form.work_order_id || null : null,
    organization_id: organizationId,
  }

  return { ok: true, payload }
}

export const buildScheduleEventForm = ({
  event = null,
  defaultEmployeeId = null,
  defaultStart = null,
  defaultEnd = null,
  defaultStatus = null,
  defaultFlexType = null,
  defaultWorkOrderId = null,
  defaultPropertyId = null,
}: BuildScheduleEventFormParams): ScheduleEventFormState => {
  const range = computeDefaultRange(defaultStart, defaultEnd)
  const resolvedWorkOrderId = event?.work_order_id ?? defaultWorkOrderId ?? ""
  const resolvedPropertyId = event?.property_id ?? defaultPropertyId ?? ""
  const timeframeStart =
    (event?.timeframe_start_at as string | null) ?? (event?.start_at as string | null) ?? null
  const timeframeEnd =
    (event?.timeframe_end_at as string | null) ?? (event?.end_at as string | null) ?? null

  return {
    employee_id: event?.employee_id ?? defaultEmployeeId ?? "",
    event_type: event?.event_type ?? ("APPOINTMENT" as ScheduleEventType),
    flex_type:
      (event?.flex_type as ScheduleEventFlexType) ??
      defaultFlexType ??
      ("HARD" as ScheduleEventFlexType),
    status: event?.status ?? defaultStatus ?? ("RESERVED" as ScheduleEventStatus),
    start_at: toInputValue(event?.start_at ?? range.start),
    end_at: toInputValue(event?.end_at ?? range.end),
    timeframe_start_at: timeframeStart ? toInputValue(timeframeStart) : toInputValue(range.start),
    timeframe_end_at: timeframeEnd ? toInputValue(timeframeEnd) : toInputValue(range.end),
    all_day: false,
    title: event?.title ?? "",
    description: event?.description ?? "",
    customer_note: event?.customer_note ?? "",
    location_text: event?.location_text ?? "",
    property_id: resolvedPropertyId,
    work_order_id: resolvedWorkOrderId,
  }
}
